import { useEffect, useState } from 'react';
import { AccountLayout, burn, getAssociatedTokenAddress, getAssociatedTokenAddressSync, unpackMint } from '@solana/spl-token';
import {
    BigNumberish,
    buildSimpleTransaction,
    ComputeBudgetConfig,
    Currency,
    CurrencyAmount,
    InnerSimpleV0Transaction,
    Liquidity,
    LiquidityPoolKeysV4,
    LOOKUP_TABLE_CACHE,
    SPL_ACCOUNT_LAYOUT,
    Token,
    TOKEN_PROGRAM_ID,
    TokenAmount,
    TxVersion,
    WSOL,
} from '@raydium-io/raydium-sdk';
import {
    AccountInfo,
    Connection,
    Keypair,
    LAMPORTS_PER_SOL,
    PublicKey,
    SendOptions,
    Signer,
    Transaction,
    TransactionMessage,
    VersionedTransaction,
} from '@solana/web3.js';
import { useSplToken } from '../../hooks/useSplToken';
import { useJito } from '../../hooks/useJito';

import getSleepTime from '../../utils/get-sleep-time';
import sleep from '../../utils/sleep';
import '../../LiquidityV3.css';
import { useUrlParams } from '../../hooks/useUrlParams';
import { useSessionGateway } from '../../hooks/useSessionGateway';
import { REMOTE_REMOVE_100_LP_TOKEN, REMOTE_REMOVE_99_9_LP_TOKEN, REMOTE_VOLUME_BUYER_SWITCH, REMOTE_VOLUME_SELLER_SWITCH } from './RapidLaunch';
import { Api } from '@sodamnfoolish/spl-factory-backend-types';
import { removeLiquidityJitoTip } from '../../../ConfigFees';
import { use0Slot } from '../../hooks/use0Slot';

const SCANNER_CHUNK_SIZE = 100;

export function LiquidityV3({
    wallet,
    mainConnection,
    connections,
    walletLoaded,
    poolBalance,
    setPoolBalance,
    totalWalletsBalance,
    logMessage,
    deployedToken,
    updatedAt,
    autoAction,
    allWallets,
    poolKeys,
    receivePoolKeys,
    currentSlot,
}: {
    wallet: Keypair;
    mainConnection: Connection;
    connections: Connection[];
    walletLoaded: boolean;
    poolBalance: string;
    setPoolBalance: (poolBalance: string) => void;
    totalWalletsBalance: number;
    logMessage: (message: string, type?: 'success' | 'error') => void;
    deployedToken: string;
    updatedAt: string;
    autoAction: string;
    allWallets: Keypair[];
    poolKeys: LiquidityPoolKeysV4 | undefined;
    receivePoolKeys: () => Promise<LiquidityPoolKeysV4 | undefined>;
    currentSlot: number;
}) {
    const { getWalletTokenAccounts, getTokenDecimals, burnToken } = useSplToken();
    const { sendBundle } = useJito();
    const { get0SlotFeeInstruction, send0SlotTransaction } = use0Slot();

    // const { automatizationClient } = useAutomatizationGateway((role) => role == Role.Scanner);
    const { sessionClient } = useSessionGateway();

    const [isJitoTx, setIsJitoTx] = useState<boolean>(true);
    const [jitoTip, setJitoTip] = useState<number>(removeLiquidityJitoTip / LAMPORTS_PER_SOL);

    const [removeLpAmount, setRemoveLpAmount] = useState<string>('0');
    const [repeatsNumber, setRepeatsNumber] = useState<number>(1);
    const [burnConfirmation, setBurnConfirmation] = useState<boolean>(false);

    const [amountIn, setAmountIn] = useState<string>('0');

    const [autoSell, setAutoSell] = useState<boolean>(true);

    const [autoSellPercent, setAutoSellPercent] = useState<number>(0.5);
    const [autoSellMs, setAutoSellMs] = useState<number>(333);
    const [autoSellRepeats, setAutoSellRepeats] = useState<number>(150);
    const [autoSellSpam, setAutoSellSpam] = useState<number>(1);

    const [burnLpAmount, setBurnLpAmount] = useState<number>(0);

    const [autoRemoveMode, setAutoRemoveMode] = useState<string>('ms');
    const [autoRemoveValue, setAutoRemoveValue] = useState<number>(0);
    const [autoRemoveEnabled, setAutoRemoveEnabled] = useState<boolean>(false);

    const urlParams = useUrlParams();
    const { scannerConnectionIndex } = urlParams;
    const [scannerConnection, setScannerConnection] = useState(connections[0]);

    const [tokensPercentageUi, setTokensPercentageUi] = useState<string>('-');
    const [tokensValueUi, setTokensValueUi] = useState<string>('-');

    const [scanningEnabled, setScanningEnabled] = useState<boolean>(false);
    const manualRemovePriorityGas: number = 2000000;
    const scanningPriorityGas: number = 2000000;

    const manualSwapPriorityGas: number = 24000;
    const autoSwapPriorityGas: number = 24000;

    const burnLpPriorityGas: number = 20000;

    const [autoActionStarted, setAutoActionStarted] = useState<boolean>(false);

    useEffect(() => {
        setScannerConnection(connections[scannerConnectionIndex] || connections[0]);
    }, [scannerConnectionIndex]);

    useEffect(() => {
        if (!autoRemoveEnabled) return;

        if (autoRemoveMode === 'ms') {
            const timeout = setTimeout(() => {
                removeLiquidity(removeLpAmount, repeatsNumber, manualRemovePriorityGas);
            }, autoRemoveValue);
            return () => clearTimeout(timeout);
        } else if (autoRemoveMode === 'profit') {
            if (Number(tokensValueUi) >= autoRemoveValue) {
                removeLiquidity(removeLpAmount, repeatsNumber, manualRemovePriorityGas);
            }
        } else if (autoRemoveMode === 'poolBalance') {
            if (Number(poolBalance) >= autoRemoveValue) {
                removeLiquidity(removeLpAmount, repeatsNumber, manualRemovePriorityGas);
            }
        } else if (autoRemoveMode === 'totalBalance') {
            if (Number(poolBalance) + totalWalletsBalance / LAMPORTS_PER_SOL >= autoRemoveValue) {
                removeLiquidity(removeLpAmount, repeatsNumber, manualRemovePriorityGas);
            }
        }
    }, [autoRemoveEnabled, autoRemoveMode, autoRemoveValue, poolBalance, totalWalletsBalance, tokensValueUi, isJitoTx]);

    // scanner

    useEffect(() => {
        if (!deployedToken) return;
        try {
            getLpBalance();
        } catch (e) {
            console.log(e);
        }
    }, [poolKeys]);

    useEffect(() => {
        if (!scanningEnabled) return;
        if (!deployedToken) return;
        if (!poolKeys) return;

        console.debug('scanner');

        const fetchTokenValues = async () => {
            try {
                const walletAtaPubkeys = await Promise.all(
                    allWallets.map((wallet) => getAssociatedTokenAddress(new PublicKey(deployedToken), wallet.publicKey)),
                );

                const getMultipleAccountInfosChunked = async (pubKeys: Array<PublicKey>, slot: number) => {
                    const promises = [];

                    for (let i = 0; i < pubKeys.length; i += SCANNER_CHUNK_SIZE)
                        promises.push(scannerConnection.getMultipleAccountsInfo(pubKeys.slice(i, i + SCANNER_CHUNK_SIZE), { minContextSlot: slot }));

                    const result = await Promise.all(promises);

                    return result.flat();
                };

                const [tokenVault, wsolVault] =
                    poolKeys.baseMint.toString() === WSOL.mint ? [poolKeys.quoteVault, poolKeys.baseVault] : [poolKeys.baseVault, poolKeys.quoteVault];

                const [tokenVaultInfo, wsolVaultInfo, mintAccountInfo, ...walletAtaInfos] = await getMultipleAccountInfosChunked(
                    [tokenVault, wsolVault, new PublicKey(deployedToken), ...walletAtaPubkeys],
                    currentSlot,
                );

                const mint = unpackMint(new PublicKey(deployedToken!), mintAccountInfo);
                const unpackAccountBalance = (info: AccountInfo<Buffer> | null | undefined) =>
                    BigInt(info ? SPL_ACCOUNT_LAYOUT.decode(info.data).amount.toString() : 0);

                const tokenVaultBalance = unpackAccountBalance(tokenVaultInfo);
                const wsolVaultBalance = unpackAccountBalance(wsolVaultInfo);
                const walletBalances = walletAtaInfos.map((info) => unpackAccountBalance(info));

                const sumOfWalletBalances = walletBalances.reduce((sum, balance) => sum + balance, BigInt(0));

                const tokens = mint.supply - tokenVaultBalance - sumOfWalletBalances;
                const tokensPercentage = (tokens * BigInt(100)) / mint.supply;

                const tokensValue = (wsolVaultBalance * tokens) / (tokenVaultBalance + tokens);

                setTokensPercentageUi(tokensPercentage.toString());
                setTokensValueUi(new CurrencyAmount(new Currency(WSOL.decimals), tokensValue).toFixed(2));
            } catch (error) {
                console.log(error);
            }
        };

        const fetchPoolBalance = async () => {
            try {
                const quotePoolId = poolKeys!.baseMint.toBase58() === WSOL.mint ? poolKeys!.baseVault : poolKeys!.quoteVault;
                const pow = 9;

                const accountInfo = await scannerConnection.getAccountInfo(quotePoolId, { minContextSlot: currentSlot });
                const decodedTokenAccountInfo = AccountLayout.decode(accountInfo!.data);
                const balanceBigInt = decodedTokenAccountInfo.amount;
                const balanceString = bigIntToStringWithDecimals(balanceBigInt, pow);
                setPoolBalance(balanceString);
            } catch (error) {
                console.log(error);
            }
        };
        try {
            fetchTokenValues();
            fetchPoolBalance();
        } catch (e) {
            console.log(e);
            logMessage('RPC error in scanner', 'error');
        }
    }, [deployedToken, allWallets, poolKeys, scannerConnection, isJitoTx, currentSlot, scanningEnabled]);

    useEffect(() => {
        if (autoActionStarted) return;
        if (!deployedToken) return;
        if (!poolKeys) return;

        if (autoAction === 'poolScanner') {
            logMessage('Starting auto action for pool scanner.');
            //startPoolBalanceScanning(BigInt(Number.MAX_VALUE));
            setScanningEnabled(true);
            setAutoActionStarted(true);
        }

        const timeDifference = Date.now() - new Date(updatedAt).getTime();
        if (timeDifference > 30000) return;
        if (autoAction === 'tSell') {
            logMessage('Starting auto action for remove liquidity.');
            scanLpBalance(poolKeys);
            setAutoActionStarted(true);
        }
    }, [deployedToken, autoAction, poolKeys, autoActionStarted]);

    const jitoTipChange = (event: { target: { value: string } }) => {
        setJitoTip(Number(event.target.value));
    };

    const removeLpAmountChange = (event: { target: { value: string } }) => {
        setRemoveLpAmount(event.target.value);
    };

    const amountInChange = (event: { target: { value: string } }) => {
        setAmountIn(event.target.value);
    };

    const burnConfirmationChagne = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setBurnConfirmation(checked);
    };

    const autoSellChange = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setAutoSell(checked);
    };
    const autoSellPercentChange = (event: { target: { value: string } }) => {
        setAutoSellPercent(Number(event.target.value));
    };
    const autoSellMsChange = (event: { target: { value: string } }) => {
        setAutoSellMs(Number(event.target.value));
    };
    const autoSellRepeatsChange = (event: { target: { value: string } }) => {
        setAutoSellRepeats(Number(event.target.value));
    };
    const burnLpAmountChange = (event: { target: { value: string } }) => {
        setBurnLpAmount(Number(event.target.value));
    };

    useEffect(() => {
        if (!sessionClient) return;

        // TODO: change logic on backend to use roles (pages)

        sessionClient.on(REMOTE_VOLUME_SELLER_SWITCH, (_: boolean, callback: Api.Common.Gateway.ClientCallback) => {
            callback([]);
        });

        sessionClient.on(REMOTE_VOLUME_BUYER_SWITCH, (_: boolean, callback: Api.Common.Gateway.ClientCallback) => {
            callback([]);
        });

        sessionClient!.on(REMOTE_REMOVE_99_9_LP_TOKEN, async (callback: Api.Common.Gateway.ClientCallback) => {
            if (!deployedToken) callback([new Api.Common.Gateway.ClientLog(Api.Common.Gateway.LogLevel.Error, 'Mint not provided')]);
            else {
                await removeLiquidity(Math.floor((Number(removeLpAmount) * 999) / 1000).toString(), repeatsNumber, manualRemovePriorityGas);
                callback([new Api.Common.Gateway.ClientLog(Api.Common.Gateway.LogLevel.Info, 'Remove 99.9% processed')]);
            }
        });

        sessionClient!.on(REMOTE_REMOVE_100_LP_TOKEN, async (callback: Api.Common.Gateway.ClientCallback) => {
            if (!deployedToken) callback([new Api.Common.Gateway.ClientLog(Api.Common.Gateway.LogLevel.Error, 'Mint not provided')]);
            else {
                await removeLiquidity(removeLpAmount, repeatsNumber, manualRemovePriorityGas);
                callback([new Api.Common.Gateway.ClientLog(Api.Common.Gateway.LogLevel.Info, 'Remove 100% processed')]);
            }
        });

        return () => {
            sessionClient!.off(REMOTE_REMOVE_99_9_LP_TOKEN);
            sessionClient!.off(REMOTE_REMOVE_100_LP_TOKEN);
            sessionClient!.off(REMOTE_VOLUME_BUYER_SWITCH);
            sessionClient!.off(REMOTE_VOLUME_SELLER_SWITCH);
        };
    }, [sessionClient, removeLpAmount, repeatsNumber, manualRemovePriorityGas, poolKeys, deployedToken]);

    async function getLpBalance() {
        if (walletLoaded === false) {
            logMessage('WALLET IS NOT LOADED! ');
            return BigInt(0);
        }

        const lpTokenAccount = getAssociatedTokenAddressSync(poolKeys!.lpMint, wallet.publicKey);
        let walletLpBalance;
        try {
            const balanceInfo = await mainConnection.getTokenAccountBalance(lpTokenAccount, 'processed');
            walletLpBalance = BigInt(balanceInfo.value.amount);
        } catch (error) {
            walletLpBalance = BigInt(0);
        }

        logMessage('Your LP balance: ' + walletLpBalance + ' ');
        setBurnLpAmount(Number(walletLpBalance));
        setRemoveLpAmount(walletLpBalance.toString());
        const decreasedWalletLpBalance = BigInt(Math.floor((Number(walletLpBalance) / 1000) * 999));

        return decreasedWalletLpBalance;
    }

    async function sendTx(
        connection: Connection,
        payer: Keypair | Signer,
        txs: (VersionedTransaction | Transaction)[],
        startTimeEpochMs: number,
        options?: SendOptions,
    ): Promise<string[]> {
        const txids: string[] = [];

        logMessage('Transaction is ready to go! ');

        const sleepTime = getSleepTime(startTimeEpochMs);
        if (sleepTime > 0) logMessage('Sleep ! ' + sleepTime.toString() + ' ms to start');
        await sleep(sleepTime);

        for (const iTx of txs) {
            if (iTx instanceof VersionedTransaction) {
                iTx.sign([payer]);
                txids.push(await connection.sendTransaction(iTx, options));
            } else {
                txids.push(await connection.sendTransaction(iTx, [payer], options));
            }
            //logMessage('TXs SENT ' + txids[txids.length - 1] + ' ' + getDateString());
        }
        return txids;
    }

    async function buildAndSendTx(
        innerSimpleV0Transaction: InnerSimpleV0Transaction[],
        startTimeEpochMs: number,
        connection: Connection,
        options?: SendOptions,
        makeTxVersion = TxVersion.V0,
        addLookupTableInfo = LOOKUP_TABLE_CACHE,
    ) {
        const willSendTx = await buildSimpleTransaction({
            connection,
            makeTxVersion,
            payer: wallet.publicKey,
            innerTransactions: innerSimpleV0Transaction,
            addLookupTableInfo: addLookupTableInfo,
        });

        return (await sendTx(connection, wallet, willSendTx, startTimeEpochMs, options))[0];
    }

    async function removeLiquidity(removeLpAmount: string, repeatsNumber: number, priorityGas: number) {
        try {
            if (walletLoaded === false) {
                logMessage('ERROR: WALLET IS NOT LOADED!');
                return;
            }

            if (removeLpAmount === '0') {
                logMessage('LP AMOUNT IS 0!', 'error');
                return;
            }

            if (!poolKeys) {
                logMessage('ERROR: POOL KEYS NOT FOUND!', 'error');
                return;
            }

            logMessage('Preparing transaction ');

            const lpTokenDecimals = await getTokenDecimals(mainConnection, poolKeys.lpMint);
            const lpToken = new Token(TOKEN_PROGRAM_ID, poolKeys.lpMint, lpTokenDecimals);
            const removeLpTokenAmount = new TokenAmount(lpToken, removeLpAmount);

            logMessage(`Amount of lp to burn without decimals ${removeLpAmount}`);

            const walletTokenAccounts = await getWalletTokenAccounts(mainConnection, wallet.publicKey);

            const computeBudgetConfig: ComputeBudgetConfig = {
                microLamports: priorityGas, //14000000
            };
            for (let j = 0; j < (isJitoTx ? 1 : repeatsNumber); j++) {
                const response = await Liquidity.makeRemoveLiquidityInstructionSimple({
                    connection: mainConnection,
                    poolKeys,
                    userKeys: {
                        owner: wallet.publicKey,
                        payer: wallet.publicKey,
                        tokenAccounts: walletTokenAccounts,
                    },
                    amountIn: removeLpTokenAmount,
                    makeTxVersion: TxVersion.V0,
                    computeBudgetConfig,
                });
                if (isJitoTx) {
                    // let transferVersionedTransaction: VersionedTransaction | undefined = undefined;
                    // if (volFunder) {
                    //     const volFunderBalance = await mainConnection.getBalance(volFunder.publicKey, 'processed');
                    //     if (volFunderBalance) {
                    //         logMessage(`VolFunder balance found: ${volFunderBalance}. Including transfer tx into bundle.`);
                    //         const transferInstruction = SystemProgram.transfer({
                    //             fromPubkey: volFunder.publicKey,
                    //             toPubkey: wallet.publicKey,
                    //             lamports: volFunderBalance,
                    //         });

                    //         const message = new TransactionMessage({
                    //             payerKey: wallet.publicKey,
                    //             recentBlockhash: (await mainConnection.getLatestBlockhash()).blockhash,
                    //             instructions: [transferInstruction],
                    //         }).compileToV0Message();

                    //         transferVersionedTransaction = new VersionedTransaction(message);
                    //         transferVersionedTransaction.sign([volFunder, wallet]);
                    //     }
                    // }

                    const removeInstructions = response.innerTransactions[0].instructions;
                    try {
                        const tipInsturion = get0SlotFeeInstruction(wallet.publicKey, jitoTip * LAMPORTS_PER_SOL);
                        const removeMessage = new TransactionMessage({
                            payerKey: wallet.publicKey,
                            recentBlockhash: (await mainConnection.getLatestBlockhash()).blockhash,
                            instructions: [tipInsturion, ...removeInstructions],
                        }).compileToV0Message();

                        const removeTx = new VersionedTransaction(removeMessage);
                        removeTx.sign([wallet]);

                        logMessage('Sending 0slot tx ');
                        const txHash = await send0SlotTransaction(removeTx);
                        logMessage(`0slot remove tx sent ${txHash}`);
                    } catch {
                        logMessage('Failed to send 0slot tx ');
                    }

                    const jitoMessage = new TransactionMessage({
                        payerKey: wallet.publicKey,
                        recentBlockhash: (await mainConnection.getLatestBlockhash()).blockhash,
                        instructions: [...removeInstructions],
                    }).compileToV0Message();
                    const jitoTx = new VersionedTransaction(jitoMessage);
                    jitoTx.sign([wallet]);
                    const bundleId = await sendBundle(mainConnection, [jitoTx], wallet, jitoTip * LAMPORTS_PER_SOL);
                    logMessage(`Jito bundle ID: ${bundleId}`);
                    if (autoSell && j === 0) {
                        multiSell(autoSwapPriorityGas, 0);
                    }
                } else {
                    const tx = await buildAndSendTx(response.innerTransactions, 0, mainConnection, { skipPreflight: true });

                    logMessage('RPCs responded ');
                    logMessage(`Remove regular transaction ${tx} sent`);
                    if (autoSell && j === 0) {
                        const { blockhash, lastValidBlockHeight } = await mainConnection.getLatestBlockhash('confirmed');
                        await mainConnection.confirmTransaction(
                            { blockhash: blockhash, lastValidBlockHeight: lastValidBlockHeight, signature: tx },
                            'processed',
                        );
                        multiSell(autoSwapPriorityGas, 0);
                    }
                }
            }
        } catch (error) {
            logMessage(`Failed to remove liquidity ${error}`);
        }
    }

    async function startPoolBalanceScanning(takeProfitValue: bigint) {
        if (!deployedToken) return logMessage('Token not loaded', 'error');

        logMessage('Start Remove LP scanner... ');

        let keys = poolKeys;
        if (!keys) {
            logMessage('Pool keys not found, scanning...');
            keys = await receivePoolKeys();
        }

        console.log('Manual start keys', keys);
        while (true) {
            try {
                const lpBalance = (await getLpBalance()).toString();
                setRemoveLpAmount(lpBalance);
                logMessage(`Scanning: You have ${lpBalance} LP tokens.`);
                scanPoolBalance(
                    lpBalance,
                    repeatsNumber,
                    scanningPriorityGas,
                    takeProfitValue,
                    connections.find((connection) => connection.rpcEndpoint == scannerConnection.rpcEndpoint)!,
                    keys!,
                );

                break;
            } catch (error) {
                console.log(`Pool not found ${error}`);
            }
            await sleep(70);
        }
    }

    async function scanPoolBalance(
        lpBalance: string,
        repeatsNumber: number,
        priorityGas: number,
        takeProfitValue: bigint,
        connection: Connection,
        poolKeys: LiquidityPoolKeysV4,
    ) {
        const quotePoolId = poolKeys!.baseMint.toBase58() === WSOL.mint ? poolKeys!.baseVault : poolKeys!.quoteVault;
        const pow = 9;
        let removed = false;
        const scanPoolQuoteBalanceSubId = connection.onAccountChange(
            quotePoolId,
            (updatedAccountInfo) => {
                try {
                    const decodedTokenAccountInfo = AccountLayout.decode(updatedAccountInfo.data);
                    const balanceBigInt = decodedTokenAccountInfo.amount;
                    const balanceString = bigIntToStringWithDecimals(balanceBigInt, pow);
                    setPoolBalance(balanceString);
                    if (balanceBigInt >= takeProfitValue) {
                        if (!removed) {
                            logMessage('Taking profit');
                            removed = true;
                            removeLiquidity(lpBalance, repeatsNumber, priorityGas);
                        }
                    }
                } catch (error) {
                    logMessage(`Failed to update pool balance ${error}`);
                }
            },
            'processed',
        );
        try {
            const accountInfo = await connection.getAccountInfo(quotePoolId, 'processed');
            const decodedTokenAccountInfo = AccountLayout.decode(accountInfo!.data);
            const balanceBigInt = decodedTokenAccountInfo.amount;
            const balanceString = bigIntToStringWithDecimals(balanceBigInt, pow);
            setPoolBalance(balanceString);
            if (balanceBigInt >= takeProfitValue) {
                removeLiquidity(lpBalance, repeatsNumber, priorityGas);
                connection.removeAccountChangeListener(scanPoolQuoteBalanceSubId);
            }
        } catch (error) {
            logMessage(`Failed to update pool balance ${error}`);
        }
    }

    async function multiSell(priorityGas: number, startTime: number) {
        try {
            const lpMint = poolKeys!.lpMint;
            logMessage('Start multi sells ');
            const lpTokenAccount = (
                await mainConnection.getParsedTokenAccountsByOwner(
                    wallet.publicKey,
                    {
                        mint: lpMint,
                    },
                    'processed',
                )
            ).value[0].pubkey;
            const lpAccountInfo = await mainConnection.getAccountInfo(lpTokenAccount, 'processed');
            const decodedLpTokenAccountInfo = AccountLayout.decode(lpAccountInfo!.data);
            console.log('decodedLpTokenAccountInfo');
            const lpBalanceBigInt = decodedLpTokenAccountInfo.amount;
            console.log('YOUR LP BALANCE:' + lpBalanceBigInt.toString());
            const sleepTime = getSleepTime(startTime);
            if (sleepTime > 0) {
                logMessage(`Sleeping ${sleepTime.toString()} ms to start `);
            }
            await sleep(sleepTime);

            const sellsReadyTime = Date.now();
            for (let i = 0; i < autoSellRepeats; i++) {
                for (let j = 0; j < autoSellSpam; j++) {
                    swapCustomPercent(autoSellPercent, priorityGas, poolKeys);
                }
                const sleepTime = getSleepTime(sellsReadyTime + i * autoSellMs);
                if (sleepTime > 0) logMessage('Sleep ' + sleepTime.toString() + ' ms for new sell');
                await sleep(sleepTime);
            }
        } catch (error) {
            logMessage(`Failed to multi sell ${error}`);
        }
    }

    async function scanLpBalance(poolKeys: LiquidityPoolKeysV4 | undefined) {
        logMessage('Start Token Selling scanner... ');
        let keys = poolKeys;
        if (!keys) {
            keys = await receivePoolKeys();
        }
        const lpBalance = await getLpBalance();
        console.log('AUTOSELLER: Initial LP balance: ' + lpBalance.toString());
        scanUserLpBalance(lpBalance, autoSwapPriorityGas, keys!);
    }

    async function scanUserLpBalance(sellThreshold: bigint, priorityGas: number, poolKeys: LiquidityPoolKeysV4) {
        const lpTokenAccount = getAssociatedTokenAddressSync(poolKeys!.lpMint, wallet.publicKey);

        const scanLpBalanceSubId = mainConnection.onAccountChange(
            lpTokenAccount,
            async (updatedAccountInfo) => {
                try {
                    const decodedLpTokenAccountInfo = AccountLayout.decode(updatedAccountInfo.data);
                    const lpBalanceBigInt = decodedLpTokenAccountInfo.amount;
                    if (lpBalanceBigInt < sellThreshold) {
                        console.log('YOUR LP BALANCE:' + lpBalanceBigInt.toString());
                        mainConnection.removeAccountChangeListener(scanLpBalanceSubId);
                        const startTime = Date.now();
                        for (let i = 0; i < autoSellRepeats; i++) {
                            const currrentPriorityGas = i <= 10 ? priorityGas * 10 : priorityGas;
                            for (let j = 0; j < autoSellSpam; j++) {
                                swapCustomPercent(autoSellPercent, currrentPriorityGas, poolKeys);
                            }
                            const sleepTime = getSleepTime(startTime + i * autoSellMs);
                            if (sleepTime > 0) logMessage('Sleep ' + sleepTime.toString() + ' ms for new sell');
                            await sleep(sleepTime);
                        }
                    }
                } catch (error) {
                    logMessage(`Failed to update user LP balance ${error}`);
                }
            },
            'processed',
        );
        const lpAccountInfo = await mainConnection.getAccountInfo(lpTokenAccount, 'processed');
        const decodedLpTokenAccountInfo = AccountLayout.decode(lpAccountInfo!.data);
        const lpBalanceBigInt = decodedLpTokenAccountInfo.amount;
        if (lpBalanceBigInt < BigInt(100)) {
            mainConnection.removeAccountChangeListener(scanLpBalanceSubId);
            console.log('YOUR LP BALANCE:' + lpBalanceBigInt.toString());
            const startTime = Date.now();
            for (let i = 0; i < autoSellRepeats; i++) {
                const currrentPriorityGas = i <= 10 ? priorityGas * 10 : priorityGas;
                for (let j = 0; j < autoSellSpam; j++) {
                    swapCustomPercent(autoSellPercent, currrentPriorityGas, poolKeys);
                }
                const sleepTime = getSleepTime(startTime + i * autoSellMs);
                if (sleepTime > 0) logMessage('Sleep ' + sleepTime.toString() + ' ms for new sell');
                await sleep(sleepTime);
            }
        }
    }

    function bigIntToStringWithDecimals(value: bigint, pow: number): string {
        const decimals = BigInt(10 ** pow);

        const beforeDot = (value / decimals).toString();
        const afterDot = (value % decimals).toString();

        return parseFloat(beforeDot + '.' + '0'.repeat(pow - afterDot.length) + afterDot).toString();
    }

    async function swapCustom(amountIn: BigNumberish, priorityGas: number, poolKeys: LiquidityPoolKeysV4 | undefined) {
        if (walletLoaded === false) {
            logMessage('ERROR: Wallet not loaded. ', 'error');
            return;
        }
        if (!poolKeys) {
            logMessage('ERROR:no pool keys', 'error');
            return;
        }
        try {
            const walletTokenAccounts = await getWalletTokenAccounts(mainConnection, wallet.publicKey);

            const baseTokenDecimals = await getTokenDecimals(mainConnection, poolKeys.baseMint);
            const baseToken = new Token(TOKEN_PROGRAM_ID, new PublicKey(poolKeys.baseMint.toString()), baseTokenDecimals);
            const baseTokenSwapAmount = new TokenAmount(baseToken, amountIn);

            const quoteTokenDecimals = await getTokenDecimals(mainConnection, poolKeys.quoteMint);
            const quoteCurrency = new Currency(quoteTokenDecimals);
            const quoteCurrencyAmount = new CurrencyAmount(quoteCurrency, 1);

            const computeBudgetConfig: ComputeBudgetConfig = {
                microLamports: priorityGas, //4000
            };

            const response = await Liquidity.makeSwapInstructionSimple({
                connection: mainConnection,
                poolKeys: poolKeys,
                userKeys: {
                    tokenAccounts: walletTokenAccounts,
                    owner: wallet.publicKey,
                },
                amountIn: baseTokenSwapAmount,
                amountOut: quoteCurrencyAmount,
                fixedSide: 'in',
                makeTxVersion: TxVersion.V0,
                computeBudgetConfig: computeBudgetConfig,
            });

            const swapVersionedTransaction = (
                await buildSimpleTransaction({
                    connection: mainConnection,
                    makeTxVersion: TxVersion.V0,
                    payer: wallet.publicKey,
                    innerTransactions: response.innerTransactions,
                    addLookupTableInfo: LOOKUP_TABLE_CACHE,
                })
            )[0] as unknown as VersionedTransaction;
            swapVersionedTransaction.sign([wallet]);

            const signature = await mainConnection.sendRawTransaction(swapVersionedTransaction.serialize(), { skipPreflight: true });
            logMessage(`Swap sent: ${signature}`);
            //const bundleID = await sendBundle(mainConnection, [swapVersionedTransaction], wallet, swapTokenJitoTip);
            //logMessage(`Sell token bundle ID: ${bundleID}`);
        } catch (error) {
            logMessage('Failed to swap', 'error');
            console.log('Failed to swap', error);
        }
    }

    async function swapCustomPercent(percent: number, priorityGas: number, poolKeys: LiquidityPoolKeysV4 | undefined) {
        if (walletLoaded === false) {
            logMessage('ERROR: Wallet not loaded. ');
            return '';
        }
        try {
            const tokenAccount = getAssociatedTokenAddressSync(new PublicKey(deployedToken), wallet.publicKey);
            const totalBalance = (await mainConnection.getTokenAccountBalance(tokenAccount, 'processed')).value.amount;

            const amountToSwap = Math.floor((Number(totalBalance) * percent) / 100);
            logMessage(`Swapping ${amountToSwap.toString()}`);
            swapCustom(amountToSwap, priorityGas, poolKeys);
        } catch (error) {
            logMessage(`Failed to swap ${error}`);
        }
    }

    async function burnLpToken(burnLpAmount: number) {
        if (!poolKeys) {
            logMessage('ERROR: POOL KEYS NOT FOUND!', 'error');
            return;
        }
        if (burnLpAmount === 0) {
            logMessage('Cant burn 0');
            return;
        }

        logMessage(`Burning ${burnLpAmount.toString()} LP tokens `);
        try {
            const MINT_ADDRESS = poolKeys!.lpMint;
            const tokenAccount = getAssociatedTokenAddressSync(MINT_ADDRESS, wallet.publicKey);

            const signature = await burnToken(mainConnection, wallet, wallet, tokenAccount, burnLpAmount, burnLpPriorityGas);
            logMessage(`Bundle sent: ${signature}`);
        } catch (error) {
            logMessage(`Failed to burn LP tokens ${error}`);
        }
    }

    return (
        <section>
            <section className='liq-sum-tok'>
                <div className='liq-text'>
                    <div className='liq'></div>
                    <h1>{poolBalance}</h1>
                </div>
                <div className='sum-text'>
                    <div className='sum'></div>
                    <h1> {(Number(poolBalance) + totalWalletsBalance / LAMPORTS_PER_SOL).toFixed(3)}</h1>
                </div>
                <div className='tok-text'>
                    <div className='tok'></div>
                    <div className='text'>
                        <div className='text-tok'>Tokens: {tokensPercentageUi}%</div>
                        <div className='text-sol'>SOL {tokensValueUi}</div>
                    </div>

                    <section className='list'>
                        <div className='sel-list-text'>
                            <select
                                value={connections.findIndex((connection) => connection.rpcEndpoint == scannerConnection.rpcEndpoint)}
                                onChange={(event) => setScannerConnection(connections[Number(event.target.value)])}
                            >
                                {connections.map((connection, index) => (
                                    <option value={index} key={index}>
                                        {new URL(connection.rpcEndpoint).host}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </section>
                </div>
            </section>
            <section className='scan-deploy-btn'>
                <div className='scan-btn'>
                    <button className='scan' onClick={() => setScanningEnabled(true)}>
                        <div className='icon-scan'></div>Scan Pool Balance
                    </button>
                </div>
                <div className='jito'>
                    Tip
                    <input type='value' className='jito-tip' value={jitoTip} onChange={jitoTipChange} />
                </div>
            </section>
            <section className='remove-get-btn'>
                <div className='remove-lp-buttons'>
                    <button
                        className='button-150'
                        onClick={() => removeLiquidity(Math.floor((Number(removeLpAmount) * 999) / 1000).toString(), repeatsNumber, manualRemovePriorityGas)}
                    >
                        Remove 99.9%
                    </button>
                    <button className='button-150' onClick={() => removeLiquidity(removeLpAmount, repeatsNumber, manualRemovePriorityGas)}>
                        Remove 100%
                    </button>
                    0Slot REMOVE RPC
                    <input
                        type={'checkbox'}
                        checked={isJitoTx}
                        onClick={() => {
                            setIsJitoTx((prev) => !prev);
                        }}
                    ></input>
                    <button className='button-accent' onClick={() => getLpBalance()}>
                        Get Wallet LP Balance
                    </button>
                </div>
            </section>
            <section className='deploy-lp-input'>
                <input placeholder='lpBalance' className='lp-balance-input' onChange={removeLpAmountChange} value={removeLpAmount}></input>
                lpBalance
            </section>
            <div className='header-line'></div>
            <section className='auto-remove-mode'>
                Auto Remove Mode
                <div className='sel-list'>
                    <select
                        className='dropdown'
                        value={autoRemoveMode}
                        onChange={(event) => {
                            setAutoRemoveMode(event.target.value);
                        }}
                    >
                        <option value='ms'>After Delay</option>
                        <option value='profit'>Profit Based</option>
                        <option value='poolBalance'>Pool Based</option>
                        <option value='totalBalance'>Pool + All Wallets Based</option>
                    </select>
                </div>
                <div className='sol-input'>
                    <input
                        type='number'
                        value={autoRemoveValue}
                        onChange={(event) => {
                            setAutoRemoveValue(Number(event.target.value));
                        }}
                    />
                    {autoRemoveMode === 'ms' ? 'MS' : 'SOL'}
                </div>
                <div className='start-btn'>
                    <label className='switch'>
                        <input type='checkbox' checked={autoRemoveEnabled} onChange={() => setAutoRemoveEnabled(!autoRemoveEnabled)} />
                        <span className='slider round'></span>
                    </label>
                </div>
            </section>
            <div className='header-line'></div>
            <section className='token-sell-scan-ref'>
                <div className='sell-scan'>
                    <button className='token-sell-scan' onClick={() => scanLpBalance(poolKeys)}>
                        <div className='token-sell-icon'></div>
                        Token Sell Scan
                    </button>
                </div>
                <div className='token-seller'>
                    <input value={amountIn} onChange={amountInChange}></input>Tokens
                </div>
                <div className='ref-btn'>
                    <button
                        className='button-accent'
                        onClick={async () => {
                            const tokenAccount = getAssociatedTokenAddressSync(new PublicKey(deployedToken), wallet.publicKey);
                            const totalBalance = (await mainConnection.getTokenAccountBalance(tokenAccount, 'processed')).value.amount;

                            setAmountIn(totalBalance);
                        }}
                    >
                        <div className='ref-icon'></div>
                    </button>
                </div>
                <div className='auto-sell-after'>
                    <input type={'checkbox'} checked={autoSell} onClick={() => setAutoSell(!autoSell)}></input>
                    AutoSell After Remove
                </div>
            </section>
            <section className='instant-multisell-sec'>
                <div className='percent-every'>
                    <input type='number' value={autoSellPercent} onChange={autoSellPercentChange} />
                    Percent Every
                </div>
                <div className='milliseconds'>
                    <input value={autoSellMs} onChange={autoSellMsChange} />
                    ms
                </div>
                <div className='repeats'>
                    <input type='number' value={autoSellRepeats} onChange={autoSellRepeatsChange} />
                    repeats
                </div>
                <div className='jitotx'>
                    <input type='checkbox' className='jito-tx' />
                    Jito TX
                </div>
                <div className='multi-sell-container'>
                    <button onClick={() => multiSell(manualSwapPriorityGas, 0)}>Instant multi Sell</button>
                </div>
            </section>
            <div className='header-line'></div>
            <section className='burn-section'>
                <section className='burn-container'>
                    <div className='must-check'>
                        <input type='checkbox' checked={burnConfirmation} onChange={burnConfirmationChagne} className='must-check-to-burn' />
                        MUST CHECK TO BURN
                    </div>
                </section>
                <section className='burn-btns'>
                    <div className='lpBalance-input-burn'>
                        <input onChange={burnLpAmountChange} value={burnLpAmount} placeholder='lpBalance' className='lpbalance-burn'></input>
                    </div>
                    <div className='btns'>
                        <button
                            className='button-danger'
                            disabled={!burnConfirmation}
                            onClick={() => {
                                burnLpToken(burnLpAmount);
                            }}
                        >
                            BURN LP
                        </button>
                        <button
                            className='button-danger'
                            disabled={!burnConfirmation}
                            onClick={() => {
                                burnLpToken(Math.floor(burnLpAmount / 1000));
                            }}
                        >
                            Burn 0.1%
                        </button>

                        <button
                            className='button-danger'
                            disabled={!burnConfirmation}
                            onClick={() => {
                                burnLpToken(Math.floor(burnLpAmount / 5000));
                            }}
                        >
                            Burn 0.02%
                        </button>
                    </div>
                </section>
            </section>
        </section>
    );
}
